import ChipSelect from "#components/ChipSelect/index.js";
import MDButton from "#components/MDButton/index.js";
import SelectionGrid from "#components/SelectionGrid/index.js";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  ButtonBase,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import Dialog from "components/NoScrollDialog";
import TranslatableTextField from "components/TranslatableTextField";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import baseEntitiesService from "services/base-entities-service";
import entityCategoriesService from "services/entity-categories-service";
import propertiesService from "services/properties-service";

const hasRules = (generic) =>
  ["number", "datetime", "datetime-auto", "boolean", "dropdown"].includes(
    generic,
  );

const operators = (generic) => {
  const ops = [{ id: "==", label: "equals" }];
  if (generic == "dropdown" || generic == "boolean") {
    return ops;
  } else if (
    generic == "number" ||
    generic == "datetime" ||
    generic == "datetime-auto"
  ) {
    ops.push({ id: "<", label: "is less than" });
    ops.push({ id: "<=", label: "is less than or equals" });
    ops.push({ id: ">", label: "exceeds" });
    ops.push({ id: ">=", label: "exceeds or equals" });
    return ops;
  }
};

const validation = {
  number: [
    {
      key: "unitId",
      optionsKey: "units",
      label: "Unit",
      type: "dropdown",
      required: false,
      xs: 4,
      crud: (state, setState) => {
        return {
          create: async (data) => {
            state = [...state];
            state.push({ id: Math.random(), ...data });
            setState(state);
            return true;
          },
          update: async (id, data) => {
            console.log("Before Updated: ", state);
            state = [...state];
            state = state.map((s) => {
              if (s.id == id) return { id, ...(s || {}), ...(data || {}) };
              return s;
            });
            setState(state);
            console.log("Updated: ", state);
            return true;
          },
          remove: async (id) => {
            state = [...state];
            state = state.filter((s) => {
              return s.id != id;
            });
            setState(state);
            return true;
          },
          refresh: async () => {},
        };
      },
    },
    {
      key: "min",
      label: "Minimum",
      type: "number",
      withUnit: "unitId",
      xs: 4,
    },
    {
      key: "max",
      label: "Maximum",
      type: "number",
      withUnit: "unitId",
      xs: 4,
    },
  ],
  text: [
    {
      key: "min",
      label: "Minimum Characters",
      type: "number",
      xs: 6,
    },
    {
      key: "max",
      label: "Maximum Characters",
      type: "number",
      xs: 6,
    },
  ],
  datetime: [
    {
      key: "min",
      optionsKey: "relativeDates",
      label: "Minimum Date Limit",
      type: "dropdown",
      required: false,
      xs: 6,
    },
    {
      key: "max",
      optionsKey: "relativeDates",
      label: "Maximum Date Limit",
      type: "dropdown",
      required: false,
      xs: 6,
    },
  ],
  dropdown: [
    { key: "options", label: "Dropdown Options", type: "optionsCRUD", xs: 12 },
  ],
  boolean: [],
  "datetime-auto": [],
  audio: [],
  image: [],
};

export default function EntitiesDialog({
  isOpen,
  onSubmit,
  data,
  mode,
  onClose,
}) {
  const { label } = useLanguage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isInitialized = useRef(false);
  const [initialEntity, setInitialEntity] = useState({});
  const [updates, setUpdates] = useState({});
  const [options, setOptions] = useState({
    baseEntities: [],
    entityCategories: [],
    units: [],
  });

  const entity = useMemo(
    () => ({ ...initialEntity, ...updates }),
    [initialEntity, updates],
  );

  useEffect(() => {
    if (isOpen && !isInitialized.current) {
      if (data?.entity) {
        setInitialEntity({
          ...data.entity,
          name: data?.entity?.allLanguageName || { en: "" },
          properties: data?.entity?.entitySheetProperties || [],
        });
      } else {
        setInitialEntity({
          name: { en: "" },
          properties: [],
        });
      }

      fetch();
      setOpen(true);
      setLoading(false);
      isInitialized.current = true;
    }

    if (!isOpen) {
      isInitialized.current = false;
      setUpdates({});
    }
  }, [isOpen, data?.entity]);

  async function fetch() {
    let [ok, data] = await propertiesService.getProperties();
    if (ok) {
      setOptions((options) => ({ ...options, properties: data }));
    }

    [ok, data] = await baseEntitiesService.getBaseEntities();
    if (ok) {
      setOptions((options) => ({ ...options, baseEntities: data }));
    }

    [ok, data] = await entityCategoriesService.getEntityCategories();
    if (ok) {
      setOptions((options) => ({ ...options, entityCategories: data }));
    }
  }

  const dropdownOptions = useMemo(() => {
    return {
      baseEntities: options.baseEntities?.length
        ? options.baseEntities.map((baseEntity) => ({
            id: baseEntity.id,
            label: baseEntity.name,
          }))
        : [],

      entityCategories: options.entityCategories?.length
        ? options.entityCategories
            // .filter((entityCategory) =>
            //   entity.baseEntityId
            //     ? entityCategory.baseEntityId == entity.baseEntityId
            //     : true,
            // )
            .map((entityCategory) => ({
              id: entityCategory.id,
              label: entityCategory.name,
              baseEntityId: entityCategory.baseEntityId,
            }))
        : [],

      properties: options.properties?.length
        ? options.properties.map((property) => ({
            id: property.id,
            label: property.name,
          }))
        : [],

      genericPropertyTypes: [
        { id: "number", label: "Number", icon: "numbers" },
        { id: "boolean", label: "Yes / No", icon: "toggle_on" },
        { id: "datetime", label: "Date / Time", icon: "calendar_month" },
        { id: "datetime-auto", label: "Auto date / time", icon: "auto_mode" },
        { id: "audio", label: "Audio", icon: "audiotrack" },
        { id: "image", label: "Picture", icon: "image" },
        { id: "dropdown", label: "Dropdown", icon: "arrow_drop_down_circle" },
        { id: "text", label: "Text", icon: "text_fields" },
      ],

      units: options.units?.length
        ? options.units.map((unit) => ({
            id: unit.id,
            label: unit.name,
          }))
        : [],
    };
  }, [options, entity.baseEntityId]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handlePropertiesChange = (newProperties) => {
    setUpdates((prev) => ({
      ...prev,
      properties: newProperties,
    }));
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();
          setLoading(true);
          console.log("Submitting entity: ", entity);
          // setLoading(false);
          // return;
          const result = await onSubmit(entity);
          if (result?.success) {
            handleClose();
          }
          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {mode === "edit" ? label("Edit Entity") : label("Create Entity")}
      </DialogTitle>

      <DialogContent
        sx={{
          minHeight: "500px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Grid container spacing={2} sx={{ pt: 1.5, flex: "0 0 auto" }}>
          <Grid item xs={8}>
            <TranslatableTextField
              autoFocus
              required
              id="name"
              name="name"
              label={label("name-singular")}
              type="text"
              values={entity.name}
              onChange={(e) => {
                setUpdates((prev) => ({
                  ...prev,
                  name: e.target.value,
                }));
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={4}>
            <DropdownSelect
              required
              label={"Base Entity"}
              initialValue={entity.baseEntityId}
              onChange={(value) => {
                if (mode == "create") {
                  const baseEntity = options.baseEntities.find(
                    (b) => b.id == value?.id,
                  );

                  setUpdates((prev) => {
                    const required = baseEntity.sheetProperties.map(
                      (sheetProperty) => {
                        return {
                          id: Date.now(),
                          new: true,
                          name: sheetProperty.name,
                          sheetPropertyId: sheetProperty.id,
                          type: 1,
                          sheetRule: {
                            enabled: false,
                          },
                        };
                      },
                    );
                    const missing = required.filter(
                      (v) =>
                        !prev?.properties?.find(
                          (t) => t.sheetPropertyId == v.sheetPropertyId,
                        ),
                    );
                    return {
                      ...prev,
                      baseEntityId: value?.id || null,
                      properties: baseEntity
                        ? [...(prev?.properties || []), ...missing]
                        : prev.properties,
                    };
                  });
                } else {
                  setUpdates((prev) => ({
                    ...prev,
                    baseEntityId: value?.id || null,
                  }));
                }
              }}
              items={dropdownOptions.baseEntities}
              fullWidth
            />
          </Grid>

          {/* <Grid item xs={3.5}>
            <DropdownSelect
              required
              label={"Category"}
              initialValue={entity.entityCategoryId}
              onChange={(value) => {
                setUpdates((prev) => ({
                  ...prev,
                  entityCategoryId: value?.id || null,
                  // baseEntityId: value?.baseEntityId || prev.baseEntityId,
                }));
              }}
              items={dropdownOptions.entityCategories}
              fullWidth
              crud={((state, setState) => ({
                create: async (data) => {
                  let [ok] = await entityCategoriesService.createEntityCategory(
                    {
                      name: data.label,
                    },
                  );
                  return ok;
                },
                update: async (id, data) => {
                  let [ok] = await entityCategoriesService.updateEntityCategory(
                    id,
                    {
                      name: data.label,
                    },
                  );
                  return ok;
                },
                remove: async (id) => {
                  let [ok] =
                    await entityCategoriesService.removeEntityCategory(id);
                  return ok;
                },
                refresh: async () => {
                  let [ok, data] =
                    await entityCategoriesService.getEntityCategories();
                  if (ok) setState(data);
                  return ok;
                },
              }))(options.entityCategories, (newData) => {
                setOptions((prev) => ({ ...prev, entityCategories: newData }));
              })}
            />
          </Grid> */}

          <SelectionGrid
            level={["account", "branchGroup", "branch"]}
            accountId={entity.accountId}
            setAccountId={(accountId) => {
              setUpdates((prev) => ({ ...prev, accountId }));
            }}
            branchGroupId={entity.branchGroupId}
            setBranchGroupId={(branchGroupId) => {
              setUpdates((prev) => ({ ...prev, branchGroupId }));
            }}
            branchId={entity.branchId}
            setBranchId={(branchId) => {
              setUpdates((prev) => ({ ...prev, branchId }));
            }}
            xs={4}
          />
        </Grid>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <DataContainer
            properties={entity.properties || []}
            onPropertiesChange={handlePropertiesChange}
            options={options}
            dropdownOptions={dropdownOptions}
            setOptions={setOptions}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {mode === "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export function DataContainer({
  properties,
  onPropertiesChange,
  dropdownOptions,
  options,
  updateRulesOnly,
  readOnly,
}) {
  const [selectedPropertyId, setSelectedPropertyId] = useState(
    properties?.[0]?.id || null,
  );
  const [filter, setFilter] = useState({
    search: "",
    measurable: true,
    actionable: true,
  });

  useEffect(() => {
    if (
      properties.length > 0 &&
      !properties.some((p) => p?.id === selectedPropertyId)
    ) {
      setSelectedPropertyId(properties[0].id);
    }
  }, [properties, selectedPropertyId]);

  const selectedProperty = useMemo(() => {
    return properties.find((p) => p?.id === selectedPropertyId) || null;
  }, [properties, selectedPropertyId]);

  const handleSaveProperty = (propertyData) => {
    const updatedProperties = properties.map((p) =>
      p?.id === propertyData.id ? propertyData : p,
    );
    onPropertiesChange(updatedProperties);
  };

  const handleDeleteProperty = (id) => {
    const updatedProperties = properties.filter((p) => p?.id !== id);
    onPropertiesChange(updatedProperties);
    if (updatedProperties.length > 0) {
      setSelectedPropertyId(updatedProperties[0].id);
    } else {
      setSelectedPropertyId(null);
    }
  };

  const handleCreateModeTransition = (type) => {
    const newProperty = {
      id: Date.now(),
      new: true,
      name: "",
      generic: "",
      type: type === "actionable" ? 2 : 1,
    };
    onPropertiesChange([...properties, newProperty]);
    setSelectedPropertyId(newProperty.id);
  };

  const hasRows = properties.length > 0;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Properties ({properties.length})</Typography>
        {updateRulesOnly ? null : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => handleCreateModeTransition("measurable")}
            >
              Create new measurable
            </MDButton>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flex: 1,
          width: "100%",
          minHeight: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {hasRows && (
          <Box
            sx={{
              width: "300px",
              borderRight: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "whitesmoke",
              overflowY: "auto",
            }}
          >
            <Box>
              <TextField
                placeholder="Search"
                variant="standard"
                fullWidth
                value={filter.search}
                onChange={(e) => {
                  setFilter((f) => ({ ...f, search: e.target.value }));
                }}
                sx={{ borderRadius: "0px !important" }}
                InputProps={{
                  sx: {
                    px: 2,
                    pr: 1,
                    py: 1,
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          filter.measurable
                            ? "Hide measurable"
                            : "Show measurable"
                        }
                      >
                        <IconButton
                          size="small"
                          color={filter.measurable ? "info" : "inherit"}
                          onClick={() =>
                            setFilter((f) => ({
                              ...f,
                              measurable: !f.measurable,
                            }))
                          }
                        >
                          <Icon>straighten</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          filter.actionable
                            ? "Hide actionable"
                            : "Show actionable"
                        }
                        color={filter.actionable ? "info" : "inherit"}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            setFilter((f) => ({
                              ...f,
                              actionable: !f.actionable,
                            }))
                          }
                        >
                          <Icon>task</Icon>
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {properties
              .filter((v) =>
                v.name
                  .toLowerCase()
                  .includes(filter.search.toLowerCase().trim()),
              )
              .filter((v) => (!filter.actionable ? v.type != 2 : true))
              .filter((v) => (!filter.measurable ? v.type != 1 : true))
              .map((row) => {
                const typeLabel =
                  row.type == 1
                    ? dropdownOptions.genericPropertyTypes?.find(
                        (t) => t.id === row?.sheetProperty?.generic,
                      )?.label || ""
                    : "Action";
                const typeIcon =
                  row.type == 1
                    ? dropdownOptions.genericPropertyTypes?.find(
                        (t) => t.id === row?.sheetProperty?.generic,
                      )?.icon || ""
                    : "task";

                return (
                  <ButtonBase
                    key={row.id}
                    sx={(theme) => ({
                      width: "100%",
                      p: 1.5,
                      textAlign: "start",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "white.main",
                      height: "40px",
                      borderLeft:
                        row.id === selectedPropertyId
                          ? `4px solid ${theme.palette.info.main}`
                          : "4px solid transparent",
                    })}
                    onClick={() => {
                      setSelectedPropertyId(row.id);
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          row.id === selectedPropertyId ? "600" : "400",
                      }}
                    >
                      {row.name || "Unnamed Property"}
                    </Typography>
                    <Tooltip title={typeLabel}>
                      <Icon fontSize="small">{typeIcon}</Icon>
                    </Tooltip>
                  </ButtonBase>
                );
              })}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            p: 2,
            overflowY: "auto",
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {selectedProperty ? (
            <PropertyDetails
              readOnly={readOnly}
              updateRulesOnly={updateRulesOnly}
              key={selectedProperty?.id}
              initialProperty={selectedProperty}
              dropdownOptions={dropdownOptions}
              options={options}
              onSave={handleSaveProperty}
              onDelete={handleDeleteProperty}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
                There are no properties yet
              </Typography>
              {updateRulesOnly ? null : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {/* <MDButton
                    onClick={() => handleCreateModeTransition("actionable")}
                    variant="outlined"
                    color="secondary"
                    size="small"
                  >
                    Create new actionable
                  </MDButton> */}
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={() => handleCreateModeTransition("measurable")}
                  >
                    Create new measurable
                  </MDButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function PropertyDetails({
  initialProperty,
  dropdownOptions,
  options,
  onSave,
  onDelete,
  updateRulesOnly,
  readOnly,
}) {
  const property = useMemo(() => {
    const prop = { ...initialProperty };
    prop.sheetProperty = options?.properties?.find(
      (v) => v.id == prop.sheetPropertyId,
    );
    return prop;
  }, [initialProperty, options?.properties]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontSize: "14px", mb: 0 }}>
              Info
            </Typography>
          </Grid>

          <Grid item xs={property.type == 2 ? 12 : 6}>
            <TextField
              required
              fullWidth
              disabled={readOnly}
              label={property.type == 2 ? "Action" : "Name"}
              multiline={property.type == 2}
              minRows={3}
              value={property.name || ""}
              onChange={
                updateRulesOnly
                  ? null
                  : (e) => onSave({ ...property, name: e.target.value })
              }
            />
          </Grid>
          {property.type == 1 || !property.type ? (
            <Grid item xs={6}>
              <DropdownSelect
                required
                disabled={readOnly}
                label={"Property"}
                initialValue={property.sheetPropertyId}
                items={dropdownOptions.properties}
                onChange={
                  updateRulesOnly
                    ? () => {}
                    : (value) => {
                        onSave({
                          ...property,
                          sheetPropertyId: value?.id || "",
                          sheetRule: { enabled: false },
                          sheetRuleId: null,
                        });
                      }
                }
                fullWidth
                disableClearable
              />
            </Grid>
          ) : null}

          {hasRules(property.sheetProperty?.generic) || property.type == 2 ? (
            <Grid item xs={12}>
              <Rules
                property={property}
                initialRule={property.sheetRule}
                readOnly={readOnly}
                onChange={(newRuleUpdates) => {
                  onSave({
                    ...property,
                    sheetRule: newRuleUpdates,
                  });
                }}
              />
            </Grid>
          ) : null}
        </Grid>
      </Box>

      {updateRulesOnly ? null : (
        <Box
          sx={{
            mt: 4,
            pt: 2,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Box>
            {property.id && onDelete && (
              <MDButton
                type="button"
                size="small"
                variant="text"
                color="error"
                disabled={readOnly}
                sx={{ textDecoration: "underline !important" }}
                onClick={() => onDelete(property.id)}
              >
                Delete Property
              </MDButton>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function Rules({ property, initialRule, onChange, readOnly }) {
  const [updates, setUpdates] = useState({});

  const rule = useMemo(() => {
    return {
      enabled: Boolean(initialRule?.enabled),
      conditions: initialRule?.sheetRuleConditions?.filter((v) => !v.isDefault)
        ?.length
        ? initialRule?.sheetRuleConditions?.filter((v) => !v.isDefault)
        : [
            {
              clauses: [{ logicalOp: "", operator: "==", value: "" }],
              result: "",
              type: 1,
              action: "",
            },
          ],
      default: initialRule?.sheetRuleConditions?.find(
        (condition) => condition.isDefault,
      ) || {
        result: "",
        type: 1,
        action: "",
      },
      // ...initialRule,
      ...updates,
    };
  }, [initialRule, updates]);

  useEffect(() => {
    const formattedRule = {
      ...initialRule,
      enabled: rule.enabled,
      sheetRuleConditions: [
        ...rule.conditions.map((v) => ({
          ...v,
          isDefault: false,
        })),
        { ...rule.default, isDefault: true },
      ],
    };

    if (JSON.stringify(initialRule) !== JSON.stringify(formattedRule)) {
      onChange(formattedRule);
    }
  }, [initialRule, rule]);

  const handleToggleEnable = (e) => {
    setUpdates((rule) => ({ ...rule, enabled: e.target.checked }));
  };

  const handleAddCondition = () => {
    setUpdates((prev) => ({
      ...prev,
      conditions: [
        ...rule.conditions,
        {
          clauses: [{ logicalOp: "", operator: "==", value: "" }],
          result: "",
          type: 1,
          action: "",
        },
      ],
    }));
  };

  const handleRemoveCondition = (index) => {
    if (rule.conditions.length <= 1) return;

    setUpdates((prev) => ({
      ...prev,
      conditions: rule.conditions.filter((_, i) => i !== index),
    }));
  };

  const handleAddClause = (conditionIndex) => {
    const updatedConditions = [...rule.conditions];
    updatedConditions[conditionIndex].clauses.push({
      logicalOp: "AND",
      operator: "==",
      value: "",
    });
    setUpdates((rule) => ({ ...rule, conditions: updatedConditions }));
  };

  const handleRemoveClause = (conditionIndex, clauseIndex) => {
    const updatedConditions = [...rule.conditions];
    updatedConditions[conditionIndex].clauses = updatedConditions[
      conditionIndex
    ].clauses.filter((_, i) => i !== clauseIndex);

    if (updatedConditions[conditionIndex].clauses.length > 0) {
      updatedConditions[conditionIndex].clauses[0].logicalOp = "";
    }
    setUpdates((rule) => ({ ...rule, conditions: updatedConditions }));
  };

  const handleClauseChange = (conditionIndex, clauseIndex, field, newValue) => {
    const updatedConditions = [...rule.conditions];
    updatedConditions[conditionIndex].clauses[clauseIndex] = {
      ...updatedConditions[conditionIndex].clauses[clauseIndex],
      [field]: newValue,
    };
    setUpdates((rule) => ({ ...rule, conditions: updatedConditions }));
  };

  const handleResultChange = (index, newScore) => {
    const updatedConditions = [...rule.conditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      result: newScore,
    };
    setUpdates((rule) => ({ ...rule, conditions: updatedConditions }));
  };

  const handleDeviationChange = (index, isDeviation) => {
    const updatedConditions = [...rule.conditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      type: isDeviation ? 2 : 1,
    };
    setUpdates((rule) => ({ ...rule, conditions: updatedConditions }));
  };

  const handleActionChange = (index, action) => {
    const updatedConditions = [...rule.conditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      action: action,
    };
    setUpdates((rule) => ({ ...rule, conditions: updatedConditions }));
  };

  useEffect(() => {
    setUpdates({});
  }, [initialRule]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={rule.enabled}
              onChange={handleToggleEnable}
              disabled={readOnly}
            />
          }
          label={"Rules"}
        />
      </Box>

      <Box
        sx={{
          opacity: rule.enabled ? 1 : 0.5,
          pointerEvents: rule.enabled ? "auto" : "none",
          ml: 2,
          filter: rule.enabled ? undefined : "blur(2px)",
        }}
      >
        <Grid container spacing={1}>
          {rule.conditions.map((condition, index) => {
            const props = {
              condition,
              index,
              handleRemoveClause,
              handleAddClause,
              handleClauseChange,
              handleResultChange,
              handleDeviationChange,
              handleRemoveCondition,
              handleActionChange,
              property,
              rule,
              readOnly,
            };
            return <RuleBubble {...props} />;
          })}

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              mb: 2,
              mt: -3,
            }}
          >
            <MDButton
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddCondition}
              disabled={readOnly}
              sx={{ mt: 1 }}
            >
              Add Condition
            </MDButton>
          </Grid>

          <Grid
            container
            spacing={1}
            component={Card}
            variant="outlined"
            sx={{
              backgroundColor: "whitesmoke",
              pb: 2,
              pr: 2,
              pl: 1,
              pt: 0,
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1.5,
                mt: 0,
              }}
            >
              <Typography
                sx={{ whiteSpace: "nowrap", minWidth: 100, fontSize: "15px" }}
              >
                Otherwise
              </Typography>
              <TextField
                placeholder="Score"
                variant="standard"
                value={rule.default?.result}
                disabled={readOnly}
                onChange={(e) =>
                  setUpdates((prev) => ({
                    ...prev,
                    default: { ...rule.default, result: e.target.value },
                  }))
                }
                InputProps={{
                  sx: {
                    px: 0.75,
                  },
                }}
              />
              <FormControlLabel
                sx={{ whiteSpace: "nowrap", ml: 1 }}
                control={
                  <Checkbox
                    checked={rule.default?.type === 2}
                    disabled={readOnly}
                    onChange={(e) =>
                      setUpdates((prev) => ({
                        ...prev,
                        default: {
                          ...rule.default,
                          type: e.target.checked ? 2 : 1,
                        },
                      }))
                    }
                  />
                }
                label="Deviation"
              />
            </Grid>
            {rule.default?.type === 2 ? (
              <Grid item xs={12}>
                <TextField
                  placeholder="Action"
                  variant="standard"
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={readOnly}
                  value={rule.default?.action}
                  InputProps={{
                    sx: {
                      px: 0.75,
                    },
                  }}
                  onChange={(e) => {
                    setUpdates((prev) => ({
                      ...prev,
                      default: {
                        ...rule.default,
                        action: e.target.value,
                      },
                    }));
                  }}
                />
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}

export function RuleBubble({
  condition,
  index,
  handleRemoveClause,
  handleAddClause,
  handleClauseChange,
  handleResultChange,
  handleDeviationChange,
  handleRemoveCondition,
  handleActionChange,
  property,
  rule,
  readOnly,
}) {
  const { clauses, result, type, action } = condition;
  const isDeviation = type === 2;

  return (
    <Grid
      item
      xs={12}
      container
      spacing={1}
      component={Card}
      variant="outlined"
      sx={{
        pb: 1,
        pr: 1,
        mb: 2,
        backgroundColor: "whitesmoke",
        position: "relative",
      }}
    >
      <Grid
        item
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
          justifyContent: "start",
        }}
      >
        <Typography
          sx={{
            whiteSpace: "nowrap",
            minWidth: 100,
            fontSize: "15px",
            height: "32px",
          }}
        >
          If{" "}
          {property.type == 1
            ? property?.name || "Property"
            : "time of completion"}
        </Typography>
        {clauses.map((clause, cIdx) => {
          if (cIdx == 0) return;
          return (
            <Box
              sx={{
                height: "32px",
              }}
            >
              <ChipSelect
                hideArrow
                value={clause.logicalOp}
                disabled={readOnly}
                onChange={(e) =>
                  handleClauseChange(index, cIdx, "logicalOp", e?.id)
                }
                items={[
                  { id: "AND", label: "AND" },
                  { id: "OR", label: "OR" },
                ]}
                renderLabel={(label) => (
                  <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                    {label}
                  </Typography>
                )}
              />
            </Box>
          );
        })}
        <Box
          sx={{
            height: "32px",
          }}
        >
          <MDButton
            size="small"
            disabled={readOnly}
            onClick={() => handleAddClause(index)}
            sx={{
              ml: 1,
              px: 1,
              minHeight: "0px",
              maxHeight: "24px !important",
              height: "24px !important",
            }}
          >
            + AND/OR
          </MDButton>
        </Box>
        <Typography
          sx={{
            whiteSpace: "nowrap",
            fontSize: "15px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: "32px",
          }}
        >
          Then
        </Typography>
      </Grid>
      <Grid
        item
        xs
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        {clauses.map((clause, cIdx) => {
          return (
            <Box
              sx={{
                width: "100%",
                height: "32px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ChipSelect
                hideArrow
                disabled={readOnly}
                items={operators(
                  property.type == 1
                    ? property?.sheetProperty?.generic
                    : "datetime",
                )}
                renderLabel={(label) => (
                  <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                    {label}
                  </Typography>
                )}
                value={clause.operator}
                onChange={(e) =>
                  handleClauseChange(index, cIdx, "operator", e?.id)
                }
              />
              {property.sheetProperty?.generic == "number" && (
                <TextField
                  placeholder="Value"
                  variant="standard"
                  disabled={readOnly}
                  value={clause.value}
                  onChange={(e) =>
                    handleClauseChange(index, cIdx, "value", e.target.value)
                  }
                  InputProps={{
                    sx: {
                      px: 0.75,
                    },
                    endAdornment: property?.sheetProperty?.unit?.name && (
                      <InputAdornment position="end">
                        <Typography
                          sx={{ fontSize: "14px", fontWeight: "400" }}
                        >
                          {property?.sheetProperty?.unit?.name}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {(property.sheetProperty?.generic == "datetime" ||
                property.sheetProperty?.generic == "datetime-auto" ||
                property.type == 2) && (
                <TimePicker
                  placeholder="Value"
                  variant="standard"
                  disabled={readOnly}
                  value={dayjs(clause.value)}
                  onChange={(e) =>
                    handleClauseChange(index, cIdx, "value", e.toISOString())
                  }
                  slotProps={{
                    textField: {
                      variant: "standard",
                      InputProps: {
                        sx: {
                          px: 0.75,
                        },
                      },
                    },
                  }}
                />
              )}

              {property.sheetProperty?.generic == "dropdown" && (
                <ChipSelect
                  disabled={readOnly}
                  items={property?.sheetProperty?.options?.map((option) => ({
                    id: option.value,
                    label: option.name,
                  }))}
                  value={clause.value}
                  onChange={(newValue) => {
                    handleClauseChange(index, cIdx, "value", newValue.id);
                  }}
                />
              )}

              {property.sheetProperty?.generic == "boolean" && (
                <ChipSelect
                  disabled={readOnly}
                  items={[
                    { id: 1, label: "Yes" },
                    { id: 2, label: "No" },
                  ]}
                  value={clause.value}
                  onChange={(newValue) => {
                    handleClauseChange(index, cIdx, "value", newValue.id);
                  }}
                />
              )}

              {cIdx > 0 && (
                <IconButton
                  onClick={() => handleRemoveClause(index, cIdx)}
                  size="small"
                  disabled={readOnly}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {cIdx == clauses.length - 1 ? null : null}
            </Box>
          );
        })}
        <Box sx={{ height: "32px" }} />
        <Box
          sx={{
            height: "32px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TextField
            placeholder="Score"
            variant="standard"
            disabled={readOnly}
            value={result}
            onChange={(e) => handleResultChange(index, e.target.value)}
            InputProps={{
              sx: {
                px: 0.75,
              },
            }}
          />

          <FormControlLabel
            sx={{ whiteSpace: "nowrap", ml: 1 }}
            control={
              <Checkbox
                size="small"
                disabled={readOnly}
                checked={isDeviation}
                onChange={(e) => handleDeviationChange(index, e.target.checked)}
              />
            }
            label="Deviation"
          />

          <IconButton
            onClick={() => handleRemoveCondition(index)}
            disabled={readOnly || rule.conditions.length <= 1}
            sx={{ position: "absolute", top: 8, right: 8 }}
            size="small"
          >
            <DeleteIcon size="small" />
          </IconButton>
        </Box>
        {isDeviation ? (
          <TextField
            sx={{ mt: 1 }}
            multiline
            minRows={2}
            fullWidth
            disabled={readOnly}
            placeholder="Action"
            value={action}
            variant="standard"
            onChange={(e) => {
              handleActionChange(index, e.target.value);
            }}
            InputProps={{
              sx: {
                px: 0.75,
              },
            }}
          />
        ) : null}
      </Grid>
    </Grid>
  );
}
